import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import './scss/Content.scss';
import {Route, Switch, Redirect, useParams} from "react-router-dom"
import Surveys from "./views/Surveys"
import Contacts from "./views/Contacts"
import UsersView from "./views/Users"
import Sidebar from "./Sidebar"
import SurveyView from "./views/SurveyView"
import useWindowDimensions from "./DataFunctions";
import {PopupElement, PopupProvider, Snackbar} from "./UIElements";
import SupportBots from "./views/SupportBots";
import {SupportBotEditor} from "./views/SupportBotEditor/SupportBotEditor";
import {SupportBotProvider} from "./views/SupportBotEditor/SupportBotData";
import BotCompetences from "./views/BotCompetences/BotCompetences";
import KnowledgeCorpusEditor from "./views/BotCompetences/KnowledgeCorpusEditor";
import ConversationAnalysis from "./views/ConversationAnalysis/ConversationAnalysis";

export default function Content() {
    const [windowSizeClass, setWindowSizeClass] = useState("")
    let contentClass = "content-container " + windowSizeClass
    const {width} = useWindowDimensions()
    useEffect(()=>{
        if (windowSizeClass !== "tablet" && width < 769) {
            setWindowSizeClass("tablet")
        }
        if (windowSizeClass === "tablet" && width > 768) {
            setWindowSizeClass("")
        }
    },[width])

    const SurveyIdRedirect = () => {
        const { surveyId } = useParams()
        return <Redirect to={`/support-campaign/${surveyId}`} />
    }

    return <SnackbarWrapper>
        <div className={contentClass}>
            <Switch>
                <Route exact path={"/"}>
                    <Redirect to={"/support-campaigns"} />
                </Route>
                <Route path={"/support-campaigns"}>
                    <Sidebar />
                    <Surveys />
                </Route>
                <Route path={"/support-bots"}>
                    <Sidebar />
                    <SupportBotProvider>
                        <SupportBots />
                    </SupportBotProvider>
                </Route>
                <Route path={"/support-bot/:botId"}>
                    <Sidebar />
                    <SupportBotProvider>
                        <PopupProvider>
                            <SupportBotEditor />
                            <PopupElement />
                        </PopupProvider>
                    </SupportBotProvider>
                </Route>
                <Route path={"/conversations"}>
                    <Sidebar />
                    <PopupProvider>
                        <ConversationAnalysis />
                        <PopupElement />
                    </PopupProvider>
                </Route>
                <Route path={"/bot-competences/knowledge/:corpusId"}>
                    <Sidebar />
                    <KnowledgeCorpusEditor />
                </Route>
                <Route path={"/bot-competences"}>
                    <Sidebar />
                    <BotCompetences />
                </Route>
                <Route path={"/contacts"}>
                    <Sidebar />
                    <PopupProvider>
                        <Contacts />
                        <PopupElement />
                    </PopupProvider>
                </Route>
                <Route path={"/users"}>
                    <Sidebar />
                    <PopupProvider>
                        <UsersView />
                        <PopupElement />
                    </PopupProvider>
                </Route>
                <Route path={'/support-campaign/:surveyId'}>
                    <Sidebar collapsed={true} />
                    <SurveyView />
                </Route>
                <Route path={"/campaign"}>
                    <Redirect to={"/support-campaigns"} />
                </Route>
                <Route path={"/campaigns"}>
                    <Redirect to={"/support-campaigns"} />
                </Route>
                <Route path={"/support-campaign"}>
                    <Redirect to={"/support-campaigns"} />
                </Route>
                <Route path={"/surveys"}>
                    <Redirect to={"/support-campaigns"} />
                </Route>
                <Route path={"/survey/:surveyId"}>
                    <SurveyIdRedirect />
                </Route>
            </Switch>
        </div>
    </SnackbarWrapper>
}

const SnackbarContext = createContext({
    show: false,
    text: "",
    state: "", //error, warning, info, success
    autoHide: true
})

function SnackbarWrapper({children}) {
    const [snackbarData, setSnackbarDataRaw] = useState(useContext(SnackbarContext))
    // Stamp every update with a monotonically increasing id so the Snackbar can tell two
    // back-to-back notifications apart even when their text/state are identical — otherwise
    // an unchanged message wouldn't refresh the auto-hide timer.
    const idRef = useRef(0)
    const setSnackbarData = useCallback((update) => {
        setSnackbarDataRaw(prev => {
            const next = typeof update === "function" ? update(prev) : update
            return {...next, _id: ++idRef.current}
        })
    }, [])
    const value = useMemo(() => (
        {snackbarData, setSnackbarData}
    ), [snackbarData, setSnackbarData])

    return <SnackbarContext.Provider value={value}>
        {children}
        <Snackbar id={snackbarData._id} show={snackbarData.show} text={snackbarData.text} state={snackbarData.state} autoHide={snackbarData.autoHide} />
    </SnackbarContext.Provider>
}

export function useSnackbar() {
    const context = useContext(SnackbarContext)
    if (context === undefined) {
        throw new Error('useSnackbar must be used inside SnackbarWrapper')
    }
    return context
}